import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Pagination } from "@mui/material";
import ProductCard from "../Cards/ProductCard";
import ViewModal from "../Modal";

const LatestProduct = ({
  addToCartProduct,
  addToWishListProduct,
  ...props
}) => {
  const [latestProducts, setLatestProducts] = useState([]);
  const [filter, setFilter] = useState({
    page: 1,
    limit: 8,
    totalRecords: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const [open, setOpen] = useState(false);
  const [state, setState] = useState({});
  const sectionRef = useRef(null);

  const handleClose = () => setOpen(false);
  const handleClickOpen = (item) => {
    setOpen(true);
    setState(item);
  };

  const handlePageChange = (event, value) => {
    setFilter((prev) => ({ ...prev, page: value }));
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const fetchLatestProducts = () => {
    setLoading(true);
    if (error) setError(false);
    axios
      .get("get-products", {
        params: { latestproducts: true, ...filter },
      })
      .then((response) => {
        const data = response?.data?.data || [];
        setLatestProducts(data.map((el) => ({ ...el, id: el._id })));
        setFilter((prev) => ({
          ...prev,
          totalRecords: response?.data?.totalRecords,
        }));
      })
      .catch(() => {
        setLatestProducts([]);
        setError(true);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchLatestProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.page]);

  const totalRecords = Number(filter?.totalRecords) || 0;
  const limit = Number(filter?.limit) || 8;
  const pageCount = Math.max(1, Math.ceil(totalRecords / limit));

  return (
    <section className="gplp-section" ref={sectionRef}>
      <div className="gplp-bg-orb gplp-bg-orb-tl" />
      <div className="gplp-bg-orb gplp-bg-orb-br" />
      <div className="gplp-bg-grid" aria-hidden="true" />

      <div className="gplp-inner">
        {/* HEADER */}
        <div className="gplp-head">
          <div className="gplp-head-text">
            <div className="gplp-eyebrow">
              <span className="gplp-eyebrow-dot" />
              JUST IN · NEW ARRIVALS
            </div>
            <h2 className="gplp-title">
              Our Latest <span className="gplp-title-accent">Gift Cards</span>
            </h2>
            <p className="gplp-sub">
              Fresh additions from your favourite global brands. Instant
              delivery, verified codes, and trusted by 10,000+ customers.
            </p>
          </div>

          <div className="gplp-head-right">
            {!loading && !error && totalRecords > 0 && (
              <div className="gplp-meta-pill">
                <strong>{totalRecords}</strong> products
              </div>
            )}
            <Link to="/shop" className="gplp-viewall">
              View all
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </Link>
          </div>
        </div>

        {/* GRID */}
        {loading ? (
          <div className="gplp-grid">
            {Array.from({ length: filter?.limit || 8 }).map((_, i) => (
              <div key={i} className="gplp-skel">
                <div className="gplp-skel-img" />
                <div className="gplp-skel-line gplp-skel-line-lg" />
                <div className="gplp-skel-line gplp-skel-line-md" />
                <div className="gplp-skel-line gplp-skel-line-sm" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="gplp-empty">
            <div className="gplp-empty-icon" aria-hidden="true">⚠</div>
            <h3>Couldn't load products</h3>
            <p>Please check your connection and try again.</p>
            <button
              type="button"
              className="gplp-empty-btn"
              onClick={fetchLatestProducts}
            >
              Try again
            </button>
          </div>
        ) : latestProducts.length === 0 ? (
          <div className="gplp-empty">
            <div className="gplp-empty-icon" aria-hidden="true">✨</div>
            <h3>No new products yet</h3>
            <p>Check back soon — fresh gift cards drop weekly.</p>
            <Link to="/shop" className="gplp-empty-btn">
              Browse all gift cards
            </Link>
          </div>
        ) : (
          <>
            <div className="gplp-grid">
              {latestProducts.map((product, index) => (
                <div
                  key={product?.id || index}
                  className="gplp-card-wrap"
                  style={{ animationDelay: `${Math.min(index * 60, 480)}ms` }}
                >
                  <div className="gplp-card-glow" aria-hidden="true" />
                  <ProductCard
                    product={product}
                    addToWishListProduct={addToWishListProduct}
                    addToCartProduct={addToCartProduct}
                    handleClickOpen={handleClickOpen}
                    carts={props.carts}
                    {...props}
                  />
                </div>
              ))}
            </div>

            {pageCount > 1 && (
              <div className="gplp-pagination">
                <Pagination
                  count={pageCount}
                  size="medium"
                  page={filter?.page || 1}
                  onChange={handlePageChange}
                  siblingCount={1}
                  shape="rounded"
                />
              </div>
            )}
          </>
        )}
      </div>

      <ViewModal
        addToCartProduct={addToCartProduct}
        open={open}
        onClose={handleClose}
        product={state}
        carts={props.carts}
        {...props}
      />

      <style>{`
        /* ============ SECTION ============ */
        .gplp-section {
          position: relative;
          background:
            linear-gradient(180deg, #0b0b0c 0%, #07060a 100%);
          padding: 70px 22px 80px;
          overflow: hidden;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          color: #fff;
        }
        .gplp-bg-orb {
          position: absolute;
          width: 380px;
          height: 380px;
          border-radius: 50%;
          filter: blur(120px);
          pointer-events: none;
          opacity: 0.6;
        }
        .gplp-bg-orb-tl {
          background: rgba(212, 168, 87, 0.22);
          top: -140px;
          left: -100px;
        }
        .gplp-bg-orb-br {
          background: rgba(212, 168, 87, 0.12);
          bottom: -160px;
          right: -100px;
        }
        .gplp-bg-grid {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(212, 168, 87, 0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(212, 168, 87, 0.04) 1px, transparent 1px);
          background-size: 48px 48px;
          mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
          -webkit-mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
          pointer-events: none;
          opacity: 0.5;
        }

        .gplp-inner {
          position: relative;
          max-width: 1300px;
          margin: 0 auto;
          z-index: 1;
        }

        /* ============ HEADER ============ */
        .gplp-head {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 18px;
          margin-bottom: 36px;
          flex-wrap: wrap;
        }
        .gplp-head-text { max-width: 720px; }
        .gplp-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 14px;
          background: rgba(212, 168, 87, 0.1);
          border: 1px solid rgba(212, 168, 87, 0.3);
          color: #d4a857;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 2px;
          margin-bottom: 14px;
        }
        .gplp-eyebrow-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #d4a857;
          box-shadow: 0 0 10px #d4a857;
          animation: gplp-pulse 1.6s ease-in-out infinite;
        }
        @keyframes gplp-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.55; transform: scale(0.85); }
        }
        .gplp-title {
          font-family: "Playfair Display", "Times New Roman", serif;
          font-size: 38px;
          font-weight: 700;
          color: #fff;
          margin: 0 0 8px;
          line-height: 1.15;
          letter-spacing: -0.4px;
        }
        .gplp-title-accent {
          background: linear-gradient(135deg, #f1cf85, #b58a3f);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          font-style: italic;
          font-weight: 600;
        }
        .gplp-sub {
          font-size: 14.5px;
          color: #9aa0a8;
          line-height: 1.55;
          margin: 0;
          max-width: 580px;
        }

        .gplp-head-right {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }
        .gplp-meta-pill {
          padding: 8px 14px;
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(212, 168, 87, 0.2);
          color: #cfd2d6;
          border-radius: 999px;
          font-size: 12px;
        }
        .gplp-meta-pill strong { color: #d4a857; font-weight: 700; margin-right: 4px; }

        .gplp-viewall {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 11px 18px;
          background: linear-gradient(135deg, #f1cf85 0%, #d4a857 50%, #b58a3f 100%);
          color: #1a1208 !important;
          border: 1px solid rgba(255, 232, 178, 0.5);
          border-radius: 999px;
          font-size: 12.5px;
          font-weight: 700;
          letter-spacing: 0.6px;
          text-decoration: none;
          box-shadow: 0 6px 16px rgba(212, 168, 87, 0.3);
          transition: transform .25s ease, box-shadow .25s ease, filter .25s ease;
        }
        .gplp-viewall:hover {
          color: #1a1208 !important;
          text-decoration: none;
          transform: translateY(-1px);
          filter: brightness(1.05);
          box-shadow: 0 10px 22px rgba(212, 168, 87, 0.45);
        }
        .gplp-viewall svg { transition: transform .2s ease; }
        .gplp-viewall:hover svg { transform: translateX(2px); }

        /* ============ GRID ============ */
        .gplp-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 22px;
        }
        .gplp-card-wrap {
          position: relative;
          opacity: 0;
          transform: translateY(14px);
          animation: gplp-fadeup .55s cubic-bezier(0.22, 1, 0.36, 1) forwards;
          transition: transform .35s cubic-bezier(0.22, 1, 0.36, 1);
          border-radius: 16px;
        }
        .gplp-card-wrap:hover { transform: translateY(-6px); }
        @keyframes gplp-fadeup {
          to { opacity: 1; transform: translateY(0); }
        }
        .gplp-card-glow {
          position: absolute;
          inset: -2px;
          border-radius: 18px;
          background: linear-gradient(135deg, rgba(212, 168, 87, 0.6), rgba(180, 130, 40, 0));
          opacity: 0;
          transition: opacity .3s ease;
          pointer-events: none;
          z-index: 0;
          filter: blur(8px);
        }
        .gplp-card-wrap:hover .gplp-card-glow { opacity: 0.55; }

        /* ----- DARK CARD STYLING (overrides on the inner ProductCard) ----- */
        .gplp-card-wrap > .col-xl-12,
        .gplp-card-wrap > [class*="col-"] {
          padding: 0 !important;
          width: 100% !important;
          max-width: 100% !important;
          flex: 0 0 100% !important;
          height: auto !important;
        }
        .gplp-card-wrap .product-item {
          position: relative;
          background: linear-gradient(180deg, #15110a 0%, #1a1208 100%) !important;
          border: 1px solid rgba(212, 168, 87, 0.18) !important;
          border-radius: 16px !important;
          overflow: hidden !important;
          box-shadow: 0 10px 24px rgba(0, 0, 0, 0.35) !important;
          transition: border-color .3s ease, box-shadow .3s ease !important;
          z-index: 1;
          height: 100%;
        }
        .gplp-card-wrap:hover .product-item {
          border-color: rgba(212, 168, 87, 0.55) !important;
          box-shadow: 0 18px 38px rgba(212, 168, 87, 0.18), 0 6px 14px rgba(0, 0, 0, 0.4) !important;
        }
        .gplp-card-wrap .product-image {
          background: #0b0b0c !important;
          padding: 14px !important;
          border-radius: 14px 14px 0 0 !important;
          position: relative;
          overflow: hidden;
        }
        .gplp-card-wrap .product-image img {
          transition: transform .5s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .gplp-card-wrap:hover .product-image img {
          transform: scale(1.04);
        }
        .gplp-card-wrap .product-info {
          padding: 14px 14px 16px !important;
          background: transparent !important;
        }
        .gplp-card-wrap .product-info h2,
        .gplp-card-wrap .product-card-title h2 {
          color: #fff !important;
        }
        .gplp-card-wrap .product-info h2 a,
        .gplp-card-wrap .product-card-title a {
          color: #fff !important;
        }
        .gplp-card-wrap .product-info h2 a:hover,
        .gplp-card-wrap .product-card-title a:hover {
          color: #d4a857 !important;
        }
        .gplp-card-wrap .product-info h6 {
          color: #9aa0a8 !important;
        }
        .gplp-card-wrap .product-info .present-price,
        .gplp-card-wrap .present-price {
          color: #d4a857 !important;
          font-weight: 700;
        }
        .gplp-card-wrap .product-info .old-price,
        .gplp-card-wrap .old-price {
          color: #6b7280 !important;
        }
        /* Tag pills on the image */
        .gplp-card-wrap .tag-wrap .tag {
          background: linear-gradient(135deg, #d4a857, #b58a3f) !important;
          color: #1a1208 !important;
          border: none !important;
          font-weight: 700 !important;
          font-size: 10px !important;
          letter-spacing: 0.4px;
          padding: 4px 10px !important;
          border-radius: 999px !important;
          box-shadow: 0 4px 10px rgba(212, 168, 87, 0.3) !important;
        }
        /* Quick-view icon button */
        .gplp-card-wrap .info-wrap button {
          background: rgba(15, 15, 16, 0.85) !important;
          border: 1px solid rgba(212, 168, 87, 0.3) !important;
          color: #d4a857 !important;
          backdrop-filter: blur(6px);
        }
        .gplp-card-wrap .info-wrap button:hover {
          background: linear-gradient(135deg, #d4a857, #b58a3f) !important;
          color: #1a1208 !important;
          border-color: transparent !important;
        }

        /* ============ SKELETON ============ */
        .gplp-skel {
          background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
          border: 1px solid rgba(212, 168, 87, 0.12);
          border-radius: 16px;
          padding: 14px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .gplp-skel-img {
          height: 200px;
          border-radius: 10px;
          background: linear-gradient(90deg, #1a1208 0%, #2a1f10 50%, #1a1208 100%);
          background-size: 200% 100%;
          animation: gplp-shimmer 1.4s linear infinite;
        }
        .gplp-skel-line {
          height: 12px;
          border-radius: 4px;
          background: linear-gradient(90deg, #1a1208 0%, #2a1f10 50%, #1a1208 100%);
          background-size: 200% 100%;
          animation: gplp-shimmer 1.4s linear infinite;
        }
        .gplp-skel-line-lg { height: 16px; width: 80%; }
        .gplp-skel-line-md { width: 55%; }
        .gplp-skel-line-sm { width: 35%; }
        @keyframes gplp-shimmer {
          from { background-position: 200% 0; }
          to { background-position: -200% 0; }
        }

        /* ============ EMPTY / ERROR ============ */
        .gplp-empty {
          background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
          border: 1px dashed rgba(212, 168, 87, 0.3);
          border-radius: 16px;
          padding: 56px 24px;
          text-align: center;
          max-width: 540px;
          margin: 0 auto;
        }
        .gplp-empty-icon {
          font-size: 36px;
          color: #d4a857;
          margin-bottom: 12px;
        }
        .gplp-empty h3 {
          font-size: 18px;
          color: #fff;
          margin: 0 0 6px;
          font-weight: 700;
        }
        .gplp-empty p {
          color: #9aa0a8;
          margin: 0 0 20px;
          font-size: 13.5px;
        }
        .gplp-empty-btn {
          display: inline-flex;
          align-items: center;
          background: linear-gradient(135deg, #d4a857, #b58a3f);
          color: #1a1208 !important;
          border: none;
          padding: 11px 22px;
          border-radius: 10px;
          font-weight: 700;
          font-size: 13px;
          letter-spacing: 0.5px;
          cursor: pointer;
          text-decoration: none;
          transition: transform .2s ease, filter .2s ease;
          font-family: inherit;
        }
        .gplp-empty-btn:hover {
          transform: translateY(-2px);
          filter: brightness(1.05);
          color: #1a1208 !important;
          text-decoration: none;
        }

        /* ============ PAGINATION ============ */
        .gplp-pagination {
          display: flex;
          justify-content: center;
          padding: 40px 0 0;
        }
        .gplp-pagination .MuiPaginationItem-root {
          color: #cfd2d6 !important;
          font-weight: 600;
          border-radius: 10px;
          margin: 0 2px;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(212, 168, 87, 0.15);
        }
        .gplp-pagination .MuiPaginationItem-root:hover {
          background: rgba(212, 168, 87, 0.15) !important;
          color: #d4a857 !important;
          border-color: rgba(212, 168, 87, 0.4);
        }
        .gplp-pagination .MuiPaginationItem-root.Mui-selected {
          background: linear-gradient(135deg, #d4a857, #b58a3f) !important;
          color: #1a1208 !important;
          border-color: transparent !important;
          box-shadow: 0 6px 14px rgba(212, 168, 87, 0.3);
        }
        .gplp-pagination .MuiPaginationItem-root.Mui-selected:hover {
          background: linear-gradient(135deg, #f1cf85, #d4a857) !important;
        }

        /* ============ RESPONSIVE ============ */
        @media (max-width: 1100px) {
          .gplp-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        }
        @media (max-width: 820px) {
          .gplp-section { padding: 56px 18px 64px; }
          .gplp-title { font-size: 28px; }
          .gplp-sub { font-size: 13.5px; }
          .gplp-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
          .gplp-skel-img { height: 160px; }
        }
        @media (max-width: 480px) {
          .gplp-section { padding: 44px 14px 52px; }
          .gplp-title { font-size: 22px; }
          .gplp-eyebrow { font-size: 10px; letter-spacing: 1.5px; }
          .gplp-grid { grid-template-columns: 1fr; }
          .gplp-head { gap: 14px; flex-direction: column; align-items: flex-start; }
          .gplp-head-right { width: 100%; }
          .gplp-viewall { flex: 1; justify-content: center; }
          .gplp-skel-img { height: 200px; }
        }
      `}</style>
    </section>
  );
};

export default LatestProduct;
